/**
 * SYM-6215. Picking a style sets the slides to the pace it stands for.
 *
 * Each style stands for a pace: Dynamic 3.2 s a slide, Professional 3.6 s,
 * Minimal 4.0 s. Until now picking one only recorded the name; the sliders
 * kept whatever the first preview had, and a slide with its own length made
 * the server ignore the style's pace. So the style's pace was dead and only
 * its transitions took effect.
 *
 * Steven's decision, 15 September 2026: picking a style sets every slide's
 * length to that style's pace, except slides whose length the customer
 * changed by hand. Those keep their value.
 *
 * The numbers are the server's (`limits.styles` on the draft, the same place
 * the character budget comes from, SYM-6196). Nothing here knows a pace of
 * its own: a server that sends no paces, or none for the chosen style, means
 * no slide is touched.
 */

import { StylePace } from 'api/video';

import { VideoScene, VideoTemplateStyle } from './types';

export type StylePaces = Partial<Record<VideoTemplateStyle, StylePace>>;

/**
 * The slide length this style stands for, or null when the server named none.
 *
 * SYM-6299 gave this a second reader: a slide added by hand starts at the
 * chosen style's length too, so what counts as a usable pace is settled here
 * once rather than in each place that wants one.
 */
export function paceFor(
  style: VideoTemplateStyle,
  paces: StylePaces | null | undefined
): number | null {
  const length = paces?.[style]?.slide_duration;

  if (typeof length !== 'number' || !Number.isFinite(length) || length <= 0) {
    return null;
  }

  return length;
}

/**
 * The slides as they should read once `style` is chosen: untouched slides at
 * the style's length, hand-set slides as they were. Returns the same array
 * when there is nothing to change, so callers can tell.
 */
export function applyStylePace(
  scenes: VideoScene[],
  style: VideoTemplateStyle,
  paces: StylePaces | null | undefined
): VideoScene[] {
  const length = paceFor(style, paces);
  if (length === null) {
    return scenes;
  }

  let changed = false;
  const next = scenes.map((scene) => {
    if (scene.durationSetByHand === true || scene.duration === length) {
      return scene;
    }
    changed = true;
    return { ...scene, duration: length };
  });

  return changed ? next : scenes;
}

/**
 * The scenes with the server's record of which lengths the customer set by
 * hand, matched by position. The scenes come from the preview composition and
 * the flag from the draft's config; they list the same slides in the same
 * order, and that is the only thing joining them.
 */
export function withHandSetDurations(
  scenes: VideoScene[],
  slides:
    | Array<{
        duration_set_by_hand?: boolean;
        transition_set_by_hand?: boolean;
      }>
    | null
    | undefined
): VideoScene[] {
  if (!Array.isArray(slides)) return scenes;

  // SYM-6265. The transition's by-hand mark travels the same way.
  return scenes.map((scene, i) => {
    const length = slides[i]?.duration_set_by_hand === true;
    const change = slides[i]?.transition_set_by_hand === true;
    if (!length && !change) return scene;

    return {
      ...scene,
      ...(length ? { durationSetByHand: true } : {}),
      ...(change ? { transitionSetByHand: true } : {}),
    };
  });
}
